import { useEffect } from 'react';
import './public-site.css';
import './privacy-page.css';

const POLICY_SECTIONS = [
  {
    title: 'Information we collect',
    content: (
      <>
        <p>We may collect information you provide when you request a vehicle, apply for or manage a rental, contact support, or sign up for messages. This can include:</p>
        <ul>
          <li>Your name, phone number, email address, and preferred contact method</li>
          <li>Driver, identity, income, insurance, and eligibility information needed to provide our services</li>
          <li>Rental, vehicle, account, payment, and customer-support information</li>
          <li>Details you include in forms, emails, calls, or text messages</li>
        </ul>
        <p>When you use our website, we may also receive basic technical information such as your browser, device, IP address, pages visited, referral source, and campaign or QR-code information.</p>
      </>
    ),
  },
  {
    title: 'How we use information',
    content: (
      <ul>
        <li>Respond to requests and provide, manage, and support Tegrat Auto services</li>
        <li>Confirm eligibility, prepare rental paperwork, and maintain customer accounts</li>
        <li>Process transactions and send rental, payment, account, and service updates</li>
        <li>Protect customers, vehicles, and our business from fraud, misuse, or security threats</li>
        <li>Maintain and improve our website, services, and customer experience</li>
        <li>Meet legal, regulatory, insurance, and recordkeeping requirements</li>
      </ul>
    ),
  },
  {
    title: 'Text messages and email',
    content: (
      <>
        <p>If you consent to receive text messages, we may send rental-related notifications and account updates. Message frequency varies, and message and data rates may apply.</p>
        <p>Reply <strong>STOP</strong> to opt out of text messages or <strong>HELP</strong> for help. Opting out of texts does not stop essential communications through other channels. See our <a href="/sms-consent">SMS consent and policy page</a> for more information.</p>
      </>
    ),
  },
  {
    title: 'How we share information',
    content: (
      <>
        <p>We may share information with service providers that help us operate our business, including providers for hosting, communications, payments, analytics, insurance, identity verification, and professional services. We may also share information when required by law, to protect rights and safety, or as part of a business transaction.</p>
        <p><strong>We do not sell personal information.</strong> We do not share mobile opt-in information or consent with third parties for their marketing purposes.</p>
      </>
    ),
  },
  {
    title: 'Data retention and security',
    content: (
      <p>We retain information for as long as reasonably necessary to provide services, maintain business and legal records, resolve disputes, and enforce agreements. We use reasonable administrative, technical, and physical safeguards, but no system can guarantee absolute security.</p>
    ),
  },
  {
    title: 'Your choices',
    content: (
      <p>You may ask us to review, correct, or delete personal information, subject to applicable law and records we must retain. You can also manage browser settings to limit cookies or similar technologies. To make a privacy request, contact us using the details below.</p>
    ),
  },
  {
    title: 'Third-party services',
    content: (
      <p>Our website may link to services we do not control, such as maps, social networks, payment providers, or vehicle-listing platforms. Their privacy practices are governed by their own policies.</p>
    ),
  },
  {
    title: 'Children’s privacy',
    content: (
      <p>Tegrat Auto services are not directed to children under 18, and we do not knowingly collect personal information from children through this website.</p>
    ),
  },
  {
    title: 'Changes to this policy',
    content: (
      <p>We may update this policy as our services or legal obligations change. We will post the revised version here and update the effective date at the top of the page.</p>
    ),
  },
];

function PrivacyPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Privacy Policy | Tegrat Auto';
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <div className="ps-root privacy-page">
      <div className="bg-deco" aria-hidden="true">
        <div className="dots" />
        <div className="blob b1" />
        <div className="blob b2" />
      </div>

      <nav className="ps-nav" aria-label="Privacy navigation">
        <a href="/" className="ps-nav-brand">
          <span className="ps-nav-mark">T</span>
          Tegrat Auto
        </a>
        <a href="/" className="privacy-home-link">Back to home</a>
      </nav>

      <main className="privacy-main">
        <header className="privacy-hero">
          <span className="eyebrow">
            <span className="e-dot"><span className="e-pulse" /></span>
            Your information
          </span>
          <h1>Privacy Policy</h1>
          <p className="privacy-intro">This policy explains what information Tegrat Auto collects, how we use and share it, and the choices available to you.</p>
          <p className="privacy-date">Effective September 5, 2026</p>
        </header>

        <div className="privacy-layout">
          <aside className="privacy-summary">
            <p className="privacy-summary-label">The short version</p>
            <p>We use your information to provide and support our vehicle services. We do not sell personal information or share mobile opt-in consent for third-party marketing.</p>
          </aside>

          <article className="privacy-content">
            {POLICY_SECTIONS.map((section) => (
              <section key={section.title}>
                <h2>{section.title}</h2>
                {section.content}
              </section>
            ))}

            <section id="contact">
              <h2>Contact us</h2>
              <p>Questions or privacy requests can be sent to:</p>
              <address>
                <strong>Tegrat Auto</strong><br />
                Email: <a href="mailto:info@tegratauto.com?subject=Privacy%20request">info@tegratauto.com</a><br />
                Phone: <a href="tel:+13177099519">(317) 709-9519</a>
              </address>
            </section>
          </article>
        </div>
      </main>

      <footer className="privacy-footer">
        <span>© 2026 Tegrat Auto · Des Dayton Digital LLC</span>
        <div>
          <a href="/support">Support</a>
          <a href="/sms-consent">SMS consent &amp; policy</a>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPage;
